import React, { useEffect, useRef, useState } from "react";
import { addMonths, isValid, differenceInDays, subDays } from "date-fns";
import type { DateRange, DateRangeWithLabel } from "@/utils/dateUtils";
import { calcZonedDate } from "@/utils/dateUtils";
import CalendarMonth from "./CalendarMonth";
import { TimeInterval } from "@/types";
import { TZDate } from "@date-fns/tz";

export const DateInputDDMMYY: React.FC<{
  initialDate: Date | null;
  timezone: string;
  onCommit: (d: Date) => void;
  first_allowed_date?: Date;
  last_allowed_date?: Date;
  className?: string;
  placeholder?: string;
}> = ({
  initialDate,
  timezone,
  onCommit,
  first_allowed_date,
  last_allowed_date,
  className,
  placeholder,
}) => {
  const [isLocalEditing, setIsLocalEditing] = useState(false);
  const [buffer, setBuffer] = useState<string>("      ");
  const [localValue, setLocalValue] = useState<string>(
    initialDate
      ? initialDate.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: timezone,
        })
      : "",
  );

  // Helpers local to this component
  const extractDigits = (value: string) => value.replace(/\D/g, "").slice(0, 6);
  const formatFromDigits = (digits: string) => {
    const buf = (digits + "      ").slice(0, 6);
    return `${buf[0]}${buf[1]}/${buf[2]}${buf[3]}/${buf[4]}${buf[5]}`;
  };
  const slashesBefore = (pos: number) => (pos > 5 ? 2 : pos > 2 ? 1 : 0);
  const digitIndexFromCaret = (caret: number) =>
    Math.max(0, Math.min(6, caret - slashesBefore(caret)));
  const caretFromDigitIndex = (digitIndex: number) => {
    const clamp = Math.max(0, Math.min(6, digitIndex));
    return clamp + (clamp >= 4 ? 2 : clamp >= 2 ? 1 : 0);
  };

  useEffect(() => {
    if (isLocalEditing) return;
    if (initialDate) {
      setLocalValue(
        initialDate.toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          timeZone: timezone,
        }),
      );
    } else {
      setLocalValue("");
    }
  }, [initialDate, timezone, isLocalEditing]);

  const onLocalFocus = () => {
    setIsLocalEditing(true);
    const init = initialDate
      ? initialDate.toLocaleString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "2-digit",
          timeZone: timezone,
        })
      : "";
    setLocalValue(init);
    const digits = extractDigits(init);
    setBuffer((digits + "      ").slice(0, 6));
  };

  const tryCommit = (current: string) => {
    // Allow single or double digits for day and month
    if (!/^\d{1,2}\/\d{1,2}\/\d{1,2}$/.test(current)) return;

    const [dd, mm, yy] = current.split("/");
    const y = 2000 + Number(yy);
    const d = Number(dd);
    const m = Number(mm);

    // Validate day and month ranges
    if (d < 1 || d > 31 || m < 1 || m > 12) return;

    // Create a date in the user's timezone at noon to avoid DST issues
    // Use TZDate from @date-fns/tz for timezone awareness
    // (Assume TZDate and timezone are available in this scope)
    const safe = new TZDate(y, m - 1, d, timezone);

    if (!isValid(safe)) return;

    // Compare in timezone-aware way (ignore time)
    const safeStartOfDay = new TZDate(
      safe.getFullYear(),
      safe.getMonth(),
      safe.getDate(),
      timezone,
    );

    if (
      first_allowed_date &&
      safeStartOfDay <
        new TZDate(
          first_allowed_date.getFullYear(),
          first_allowed_date.getMonth(),
          first_allowed_date.getDate(),
          timezone,
        )
    ) {
      return;
    }
    if (
      last_allowed_date &&
      safeStartOfDay >
        new TZDate(
          last_allowed_date.getFullYear(),
          last_allowed_date.getMonth(),
          last_allowed_date.getDate(),
          timezone,
        )
    ) {
      return;
    }

    // Don't commit if value is the same as initialDate (ignoring time, in timezone)
    if (
      initialDate &&
      safeStartOfDay.getFullYear() === initialDate.getFullYear() &&
      safeStartOfDay.getMonth() === initialDate.getMonth() &&
      safeStartOfDay.getDate() === initialDate.getDate()
    ) {
      return;
    }
    onCommit(safeStartOfDay);
  };

  const onLocalBlur = () => {
    setIsLocalEditing(false);
    // Normalize the value before committing
    const normalizedValue = localValue.replace(/\s+/g, "");
    tryCommit(normalizedValue);
  };

  const onLocalPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!isLocalEditing) return;
    e.preventDefault();
    const text = e.clipboardData?.getData("text") ?? "";
    const digits = extractDigits(text);
    const next = formatFromDigits(digits);
    setLocalValue(next);
    setBuffer((digits + "      ").slice(0, 6));
  };

  const onLocalKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isLocalEditing) return;
    const input = event.currentTarget as HTMLInputElement;
    const caret = input.selectionStart ?? localValue.length;
    const digitIdx = digitIndexFromCaret(caret);
    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      const newDigit = event.key;
      const arr = buffer.split("");
      arr[digitIdx] = newDigit;
      if (digitIdx === 1) {
        const d1 = arr[0] === " " ? "0" : arr[0];
        const d2 = arr[1] === " " ? "0" : arr[1];
        const ddNum = parseInt(d1 + d2, 10);
        if (ddNum < 1 || ddNum > 31) {
          arr[0] = "0";
          arr[1] = d1;
          if (arr[2] === " ") arr[2] = newDigit;
          else if (arr[3] === " ") arr[3] = newDigit;
          else arr[4] = newDigit;
        }
      }
      if (digitIdx === 3) {
        const m1 = arr[2] === " " ? "0" : arr[2];
        const m2 = arr[3] === " " ? "0" : arr[3];
        const mmNum = parseInt(m1 + m2, 10);
        if (mmNum < 1 || mmNum > 12) {
          arr[2] = "0";
          arr[3] = m1;
          arr[4] = newDigit;
        }
      }
      const nextBuf = arr.join("");
      const nextValue = `${nextBuf[0]}${nextBuf[1]}/${nextBuf[2]}${nextBuf[3]}/${nextBuf[4]}${nextBuf[5]}`;
      const ddDone = nextBuf[0] !== " " && nextBuf[1] !== " ";
      if (ddDone) {
        const ddNum = parseInt(nextBuf[0] + nextBuf[1], 10);
        if (isNaN(ddNum) || ddNum < 1 || ddNum > 31) return;
      }
      const mmDone = nextBuf[2] !== " " && nextBuf[3] !== " ";
      if (mmDone) {
        const mmNum = parseInt(nextBuf[2] + nextBuf[3], 10);
        if (isNaN(mmNum) || mmNum < 1 || mmNum > 12) return;
      }
      let nextDigitIndex = digitIdx + 1;
      if (digitIdx === 1 && (arr[2] === newDigit || arr[3] === newDigit))
        nextDigitIndex = arr[3] === newDigit ? 4 : 3;
      if (digitIdx === 3 && arr[4] === newDigit) nextDigitIndex = 5;
      const nextCaret = caretFromDigitIndex(nextDigitIndex);
      setLocalValue(nextValue);
      setBuffer(nextBuf);
      requestAnimationFrame(() => input.setSelectionRange(nextCaret, nextCaret));
      if (nextValue.length === 8) tryCommit(nextValue);
      return;
    }
    if (event.key === "Backspace") {
      event.preventDefault();
      const removeIdx = digitIdx > 0 ? digitIdx - 1 : 0;
      const nextBuf = buffer.slice(0, removeIdx) + " " + buffer.slice(removeIdx + 1);
      const nextValue = `${nextBuf[0]}${nextBuf[1]}/${nextBuf[2]}${nextBuf[3]}/${nextBuf[4]}${nextBuf[5]}`;
      const nextCaret = caretFromDigitIndex(removeIdx);
      setLocalValue(nextValue);
      setBuffer(nextBuf);
      requestAnimationFrame(() => input.setSelectionRange(nextCaret, nextCaret));
      return;
    }
    if (event.key === "Delete") {
      event.preventDefault();
      const nextBuf = buffer.slice(0, digitIdx) + " " + buffer.slice(digitIdx + 1);
      const nextValue = `${nextBuf[0]}${nextBuf[1]}/${nextBuf[2]}${nextBuf[3]}/${nextBuf[4]}${nextBuf[5]}`;
      const nextCaret = caretFromDigitIndex(digitIdx);
      setLocalValue(nextValue);
      setBuffer(nextBuf);
      requestAnimationFrame(() => input.setSelectionRange(nextCaret, nextCaret));
      return;
    }
    if (event.key === "Enter") {
      tryCommit(localValue);
      (event.currentTarget as HTMLInputElement).blur();
      return;
    }
    if (event.key === "Escape") {
      setIsLocalEditing(false);
      if (initialDate) {
        setLocalValue(
          initialDate.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            timeZone: timezone,
          }),
        );
      } else {
        setLocalValue("");
      }
      (event.currentTarget as HTMLInputElement).blur();
      return;
    }
    if (event.key.length === 1 && !/\d/.test(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <input
      type="text"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={onLocalFocus}
      onBlur={onLocalBlur}
      onKeyDown={onLocalKeyDown}
      onPaste={onLocalPaste}
      placeholder={placeholder || "DD/MM/YY"}
      inputMode="numeric"
      pattern="\d{1,2}/\d{1,2}/\d{1,2}"
      className={className}
    />
  );
};

interface Props {
  dateRange: DateRangeWithLabel | null;
  setDateRange: React.Dispatch<React.SetStateAction<DateRangeWithLabel | null>>;
  aggregate?: number | "";
  defaultMonth?: Date;
  aggregateDateChunks?: DateRange[] | null;
  first_allowed_date?: Date;
  last_allowed_date?: Date;
  timezone: string;
  compare?: boolean;
  compareDate?: DateRangeWithLabel | null;
  onChangeCompareDate?: (compareDate: DateRangeWithLabel | null) => void;
  singleSelect?: boolean;
  hideInlineDateInputs?: boolean;
}

const Calendar = ({
  dateRange,
  setDateRange,
  aggregate,
  defaultMonth,
  aggregateDateChunks,
  first_allowed_date,
  last_allowed_date,
  timezone,
  compare,
  compareDate,
  onChangeCompareDate,
  singleSelect = false,
  hideInlineDateInputs = false,
}: Props) => {
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  // compare syncing is driven by calendar clicks only
  const isInternalChangeRef = useRef(false);
  const getDateMonth = (date: Date) =>
    new TZDate(date.getFullYear(), date.getMonth(), 1, timezone);

  const calculateMonthsDiff = (date1: Date, date2: Date): number =>
    (date2.getFullYear() - date1.getFullYear()) * 12 +
    (date2.getMonth() - date1.getMonth());

  const [monthDates, setMonthDates] = useState(() => {
    const baseMonthDate = getDateMonth(defaultMonth || dateRange?.from || new Date());
    // Adjust initial months based on allowed date range
    const months = [];
    if (first_allowed_date && last_allowed_date) {
      // populates months from first allowed to last allowed months
      const firstMonth = getDateMonth(first_allowed_date);
      const lastMonth = getDateMonth(last_allowed_date);
      let currentMonth = firstMonth;
      while (currentMonth <= lastMonth) {
        months.push(new TZDate(currentMonth, timezone));
        currentMonth = new TZDate(
          calcZonedDate(currentMonth, timezone, addMonths, 1),
          timezone,
        );
      }
      return months;
    }
    if (!first_allowed_date && last_allowed_date) {
      // Populates months from either baseMonthDate or 12 months before last allowed date,
      // whichever is earlier
      const lastMonth = getDateMonth(last_allowed_date);
      const numMonths =
        baseMonthDate.getTime() < lastMonth.getTime()
          ? Math.max(calculateMonthsDiff(baseMonthDate, lastMonth) + 1, 12)
          : 12;
      for (let i = 0; i < numMonths; i++) {
        months.unshift(
          new TZDate(lastMonth.getFullYear(), lastMonth.getMonth() - i, timezone),
        );
      }
      return months;
    }
    if (first_allowed_date && !last_allowed_date) {
      // Shows months from today  to backwards:
      // - If first_allowed_date is less than 12 months ago: shows up to first_allowed_date
      // - If first_allowed_date is more than 12 months ago: shows max(12 months, months of base date from today)
      const firstMonth = getDateMonth(first_allowed_date);
      const currentMonth = getDateMonth(new Date());

      const numMonths = Math.min(
        Math.max(calculateMonthsDiff(baseMonthDate, currentMonth) + 1, 12),
        calculateMonthsDiff(firstMonth, currentMonth) + 1,
      );

      // Generate months array from current month backwards
      for (let i = 0; i < numMonths; i++) {
        months.push(
          new TZDate(currentMonth.getFullYear(), currentMonth.getMonth() - i, timezone),
        );
      }
      return months;
    }
    // Default case: No date restrictions
    // Populates months from current month backwards, showing at least 12 months
    // or more if baseMonthDate is earlier than current month
    const currentMonth = getDateMonth(new Date());
    const monthsDiff = calculateMonthsDiff(baseMonthDate, currentMonth) + 1;
    const numMonths = Math.max(monthsDiff, 12);
    for (let i = 0; i < numMonths; i++) {
      months.unshift(
        new TZDate(currentMonth.getFullYear(), currentMonth.getMonth() - i, timezone),
      );
    }
    return months;
  });
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const monthRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Helper to compute auto compare range from current range
  const computeAutoCompare = (
    currentRange: DateRangeWithLabel,
  ): DateRangeWithLabel | null => {
    if (!currentRange?.from || !currentRange?.to) return null;

    const daysDiff = differenceInDays(currentRange.to, currentRange.from) + 1;

    let compareFrom, compareTo;
    if (daysDiff <= 7) {
      compareFrom = calcZonedDate(currentRange.from, timezone, subDays, 7);
      compareTo = calcZonedDate(currentRange.to, timezone, subDays, 7);
    } else {
      compareFrom = calcZonedDate(currentRange.from, timezone, subDays, daysDiff);
      compareTo = calcZonedDate(currentRange.to, timezone, subDays, daysDiff);
    }

    if (
      first_allowed_date &&
      (compareFrom < first_allowed_date || compareTo < first_allowed_date)
    ) {
      compareTo = calcZonedDate(currentRange.from, timezone, subDays, 1);
      if (daysDiff === 1) {
        compareFrom = compareTo;
      } else {
        compareFrom = calcZonedDate(compareTo, timezone, subDays, daysDiff - 1);
      }
      if (compareFrom < first_allowed_date) compareFrom = first_allowed_date;
    }

    if (
      first_allowed_date &&
      currentRange.from.getTime() === first_allowed_date.getTime()
    )
      return null;

    return { from: compareFrom, to: compareTo, label: TimeInterval.CUSTOM };
  };

  const handleDateClick = (date: Date) => {
    // Check if date is within allowed range
    if (first_allowed_date && date < first_allowed_date) return;
    if (last_allowed_date && date > last_allowed_date) return;

    isInternalChangeRef.current = true;

    if (singleSelect) {
      // In single select mode, always set both from and to to the clicked date
      const nextRange = { from: date, to: date, label: TimeInterval.CUSTOM };
      setDateRange(nextRange);
      setTempStartDate(null);
      if (compare && onChangeCompareDate) {
        const auto = computeAutoCompare(nextRange);
        if (auto) onChangeCompareDate(auto);
      }
    } else {
      // Original range selection logic
      if (!tempStartDate) {
        setTempStartDate(date);
        const nextRange = { from: date, to: date, label: TimeInterval.CUSTOM };
        setDateRange(nextRange);
        if (compare && onChangeCompareDate) {
          const auto = computeAutoCompare(nextRange);
          if (auto) onChangeCompareDate(auto);
        }
      } else {
        const newStartDate = tempStartDate < date ? tempStartDate : date;
        const newEndDate = tempStartDate < date ? date : tempStartDate;
        const nextRange = {
          from: newStartDate,
          to: newEndDate,
          label: TimeInterval.CUSTOM,
        };
        setDateRange(nextRange);
        if (compare && onChangeCompareDate) {
          const auto = computeAutoCompare(nextRange);
          if (auto) onChangeCompareDate(auto);
        }
        setTempStartDate(null);
      }
    }
    // Reset the internal change flag after a short delay
    setTimeout(() => {
      isInternalChangeRef.current = false;
    }, 0);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const container = event.currentTarget;

    // Add months at the start when scrolling up
    if (container.scrollTop < 150) {
      const firstDate = monthDates[0];
      const newDate1 = new TZDate(
        firstDate.getFullYear(),
        firstDate.getMonth() - 1,
        timezone,
      );
      const newDate2 = new TZDate(
        firstDate.getFullYear(),
        firstDate.getMonth() - 2,
        timezone,
      );
      const newDates: Date[] = [];

      // Check each date individually against the allowed range
      if (
        !first_allowed_date ||
        newDate1 >=
          new TZDate(
            first_allowed_date.getFullYear(),
            first_allowed_date.getMonth(),
            timezone,
          )
      ) {
        newDates.push(newDate1);
        // Only check newDate2 if newDate1 was valid
        if (
          !first_allowed_date ||
          newDate2 >=
            new TZDate(
              first_allowed_date.getFullYear(),
              first_allowed_date.getMonth(),
              timezone,
            )
        ) {
          newDates.unshift(newDate2);
        }
      }
      if (newDates.length > 0) {
        setMonthDates((prev) => [...(newDates as TZDate[]), ...prev]);
      }
    }

    // Add months at the end when scrolling near bottom
    const scrolledToBottom =
      container.scrollHeight - container.scrollTop <= container.clientHeight + 150;
    if (scrolledToBottom) {
      const lastDate = monthDates[monthDates.length - 1];
      const newDate1 = new TZDate(
        lastDate.getFullYear(),
        lastDate.getMonth() + 1,
        timezone,
      );
      const newDate2 = new TZDate(
        lastDate.getFullYear(),
        lastDate.getMonth() + 2,
        timezone,
      );
      const newDates: Date[] = [];

      // Check each date individually against the allowed range
      if (
        !last_allowed_date ||
        newDate1 <=
          new TZDate(
            last_allowed_date.getFullYear(),
            last_allowed_date.getMonth(),
            timezone,
          )
      ) {
        newDates.push(newDate1);
        // Only check newDate2 if newDate1 was valid
        if (
          !last_allowed_date ||
          newDate2 <=
            new TZDate(
              last_allowed_date.getFullYear(),
              last_allowed_date.getMonth(),
              timezone,
            )
        ) {
          newDates.push(newDate2);
        }
      }
      if (newDates.length > 0) {
        setMonthDates((prev) => [...prev, ...(newDates as TZDate[])]);
      }
    }
  };

  // Helper function to get month key
  const getMonthKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}`;

  // One-time/enable-time compare sync based on current range
  useEffect(() => {
    if (compare && !compareDate && dateRange && onChangeCompareDate) {
      const auto = computeAutoCompare(dateRange);
      if (auto) onChangeCompareDate(auto);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compare]);

  // Enforce compare-off when current range starts at first_allowed_date
  useEffect(() => {
    if (!first_allowed_date) return;
    const start = dateRange?.from;
    if (start && start.getTime() === first_allowed_date.getTime()) {
      // Consumers should turn off compare when impossible; emit null compareRange
      onChangeCompareDate?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange?.from, first_allowed_date]);

  // Enforce single date selection when singleSelect is true
  useEffect(() => {
    if (singleSelect && dateRange?.from && dateRange?.to) {
      const fromTime = dateRange.from.getTime();
      const toTime = dateRange.to.getTime();
      if (fromTime !== toTime) {
        // If from and to differ, set both to from date
        setDateRange({
          ...dateRange,
          to: dateRange.from,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleSelect, dateRange?.from, dateRange?.to]);

  useEffect(() => {
    // Skip scrolling if this is an internal change
    if (!scrollableContainerRef.current || isInternalChangeRef.current) return;

    // If there's a dateRange, scroll to make the start date visible
    if (dateRange?.from) {
      // Find the month that contains the start date
      const startMonth = new TZDate(
        dateRange.from.getFullYear(),
        dateRange.from.getMonth(),
        1,
        timezone,
      );
      const monthKey = getMonthKey(startMonth);
      const monthElement = monthRefs.current[monthKey];

      if (monthElement) {
        // Scroll the month into view with some offset
        monthElement.scrollIntoView();
        // Add a small offset to show the previous month partially
        if (scrollableContainerRef.current) {
          scrollableContainerRef.current.scrollTop -= 50;
        }
      } else {
        // Default scroll if month not found
        scrollableContainerRef.current.scrollTop = 200;
      }
    } else {
      // If there's a last allowed date, scroll to the end
      if (last_allowed_date) {
        const lastMonth = new TZDate(
          last_allowed_date.getFullYear(),
          last_allowed_date.getMonth(),
          1,
          timezone,
        );
        const monthKey = getMonthKey(lastMonth);
        const monthElement = monthRefs.current[monthKey];
        if (monthElement) {
          monthElement.scrollIntoView();
        }
      } else {
        // Default scroll if no dateRange
        scrollableContainerRef.current.scrollTop = 200;
      }
    }
  }, [dateRange?.from, last_allowed_date, timezone]);

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      {!hideInlineDateInputs && compare && (
        <div className="min-h-[60px] flex flex-col pl-[34px] pt-[9px] pr-[9px] pb-[7px] border-b-[.9px] border-b-[#E1E6ED] justify-end ">
          <span className="text-[11px] text-brand-primary-new/60 px-1">Compare</span>
          <div className="flex items-center gap-2 text-[14.4px] font-normal leading-[17.241px] tracking-[0.288px]">
            <div className="w-fit">
              <DateInputDDMMYY
                initialDate={compareDate?.from ?? null}
                timezone={timezone}
                first_allowed_date={first_allowed_date}
                last_allowed_date={last_allowed_date}
                onCommit={(d) => {
                  if (!onChangeCompareDate || !compareDate) return;
                  const nextTo =
                    compareDate.to && compareDate.to < d ? d : compareDate.to || d;
                  onChangeCompareDate({
                    from: d,
                    to: nextTo,
                    label: TimeInterval.CUSTOM,
                  });
                }}
                placeholder="DD/MM/YY"
                className="p-1 w-[100px] bg-transparent text-[14px] text-brand-primary-new/80 placeholder-[#A0ABBC] outline-none rounded-sm"
              />
            </div>
            <div>-</div>
            <div className="w-fit">
              <DateInputDDMMYY
                initialDate={compareDate?.to ?? null}
                timezone={timezone}
                first_allowed_date={first_allowed_date}
                last_allowed_date={last_allowed_date}
                onCommit={(d) => {
                  if (!onChangeCompareDate || !compareDate) return;
                  const nextFrom =
                    compareDate.from && compareDate.from > d ? d : compareDate.from || d;
                  onChangeCompareDate({
                    from: nextFrom,
                    to: d,
                    label: TimeInterval.CUSTOM,
                  });
                }}
                placeholder="DD/MM/YY"
                className="p-1 pl-2 bg-transparent text-[14px] text-brand-primary-new/80 placeholder-[#A0ABBC] outline-none rounded-sm"
              />
            </div>
          </div>
        </div>
      )}
      {!hideInlineDateInputs && (
        <div className="min-h-[60px] flex flex-col pl-[34px] pt-[9px] pr-[9px] pb-[7px] justify-end">
          {compare && (
            <span className="text-[11px] text-brand-primary-new/60 px-1">Current</span>
          )}
          <div className="flex items-center gap-2 text-[14.4px] font-normal leading-[17.241px] tracking-[0.288px] ">
            <div className="w-fit">
              <DateInputDDMMYY
                initialDate={dateRange?.from ?? null}
                timezone={timezone}
                first_allowed_date={first_allowed_date}
                last_allowed_date={last_allowed_date}
                onCommit={(d) =>
                  setDateRange((prev) => {
                    if (singleSelect) {
                      // In single select mode, both from and to are the same
                      return {
                        from: d,
                        to: d,
                        label: TimeInterval.CUSTOM,
                      };
                    } else {
                      // Original range logic
                      const previousTo = prev?.to || d;
                      const nextTo = previousTo < d ? d : previousTo;
                      return {
                        from: d,
                        to: nextTo,
                        label: TimeInterval.CUSTOM,
                      };
                    }
                  })
                }
                placeholder="DD/MM/YY"
                className="p-1 w-[100px] bg-transparent text-[14px] text-brand-primary-new/80 placeholder-[#A0ABBC] outline-none rounded-sm"
              />
            </div>
            {!singleSelect && <div>-</div>}
            {!singleSelect && (
              <div className="w-fit">
                <DateInputDDMMYY
                  initialDate={dateRange?.to ?? null}
                  timezone={timezone}
                  first_allowed_date={first_allowed_date}
                  last_allowed_date={last_allowed_date}
                  onCommit={(d) =>
                    setDateRange((prev) => {
                      const previousFrom = prev?.from || d;
                      const nextFrom = previousFrom > d ? d : previousFrom;
                      const nextRange = {
                        from: nextFrom,
                        to: d,
                        label: TimeInterval.CUSTOM,
                      };
                      return nextRange;
                    })
                  }
                  placeholder="DD/MM/YY"
                  className="p-1 pl-2 bg-transparent text-[14px] text-brand-primary-new/80 placeholder-[#A0ABBC] outline-none rounded-sm"
                />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="relative grid grid-cols-7 gap-y-1 pl-[11.45px] pr-[11.45px] border-t-[0.9px] border-border-gray-dark bg-[#FBFBFB]">
        {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
          <div
            key={index}
            className="flex p-[8.1px] flex-col justify-center items-center gap-[8.1px] text-[#617390] text-[10.8px] font-medium leading-[12.96px] tracking-[0.216px]"
          >
            {day}
          </div>
        ))}
        <div className="absolute left-0 right-0 bottom-0 h-12 bg-gradient-to-b from-white to-transparent translate-y-full pointer-events-none border-t-[0.9px] border-t-border-gray-dark"></div>
      </div>
      <div
        ref={scrollableContainerRef}
        onScrollCapture={handleScroll}
        className="flex-1 flex flex-col gap-[18px] overflow-auto scrollbar-hide"
      >
        {monthDates.map((date) => (
          <div
            key={date.toString()}
            ref={(el) => (monthRefs.current[getMonthKey(date)] = el)}
          >
            <CalendarMonth
              date={date}
              handleDateClick={handleDateClick}
              dateRange={dateRange}
              aggregate={aggregate}
              aggregateDateChunks={aggregateDateChunks}
              first_allowed_date={first_allowed_date}
              last_allowed_date={last_allowed_date}
              timezone={timezone}
              compareDate={compareDate}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Calendar;
